import { motion, AnimatePresence } from 'framer-motion';
import { useEffect, useState } from 'react';
import { useConfetti } from '@/hooks/useConfetti';
import { useLanguage } from '@/i18n/LanguageContext';
import AchievementBadge from './AchievementBadge';

interface Milestone {
  id: string;
  title: string;
  description: string;
  achievementId?: string;
}

interface MilestoneOverlayProps {
  milestone: Milestone | null;
  onDismiss: () => void;
}

const MilestoneOverlay = ({ milestone, onDismiss }: MilestoneOverlayProps) => {
  const [show, setShow] = useState(false);
  const { fire } = useConfetti();
  const { t } = useLanguage();

  useEffect(() => {
    if (milestone) {
      setShow(true);
      fire({ particleCount: 80, x: window.innerWidth / 2, y: window.innerHeight / 2, spread: 120 });
      const t = setTimeout(() => {
        setShow(false);
        setTimeout(onDismiss, 500);
      }, 5000);
      return () => clearTimeout(t);
    }
  }, [milestone]);

  return (
    <AnimatePresence>
      {show && milestone && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-background/80 backdrop-blur-sm"
          onClick={() => { setShow(false); setTimeout(onDismiss, 300); }}
        >
          <motion.div
            initial={{ scale: 0.8, y: 30 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.9, y: -20 }}
            transition={{ type: 'spring', stiffness: 200 }}
            className="text-center space-y-6 p-10 max-w-sm"
          >
            {milestone.achievementId && (
              <div className="flex justify-center">
                <AchievementBadge achievementId={milestone.achievementId} unlocked size="lg" animate />
              </div>
            )}

            <div>
              <p className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground/30 mb-3">
                {t('milestone_reached') || 'milestone reached'}
              </p>
              <h2 className="text-xl font-extralight tracking-wide text-foreground/90">
                {t(`milestone_${milestone.id}`) || milestone.title}
              </h2>
            </div>

            <p className="text-sm text-muted-foreground/40 font-light leading-relaxed">
              {t(`milestone_${milestone.id}_desc`) || milestone.description}
            </p>

            <p className="text-[10px] text-muted-foreground/15 tracking-widest">
              {t('tap_to_continue') || 'tap to continue'}
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default MilestoneOverlay;
